import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { QUICK_STRIP_SECTION_META, SERVICES_GRID_META } from '@/data/homePageSectionMeta';
import { mergeQuickStripListForAdmin } from '@/lib/homePageVisibility';

function mergeToggleList(meta, stored) {
  const map = new Map();
  (stored || []).forEach((x) => {
    if (x?.id) map.set(x.id, x.enabled !== false);
  });
  return meta.map(({ id }) => ({
    id,
    enabled: map.has(id) ? map.get(id) : true,
  }));
}

const HomePageStripAndServicesTab = ({ settings, updateSetting }) => {
  const { t } = useTranslation();
  const [dragStripIndex, setDragStripIndex] = useState(null);

  const quickList = useMemo(
    () => mergeQuickStripListForAdmin(QUICK_STRIP_SECTION_META, settings?.home_quick_categories),
    [settings?.home_quick_categories]
  );

  const quickMetaById = useMemo(
    () => new Map(QUICK_STRIP_SECTION_META.map((m) => [m.id, m])),
    []
  );

  const servicesList = useMemo(
    () => mergeToggleList(SERVICES_GRID_META, settings?.home_services_items),
    [settings?.home_services_items]
  );

  const setQuickEnabled = (id, enabled) => {
    const next = quickList.map((row) => (row.id === id ? { ...row, enabled } : row));
    updateSetting('home_quick_categories', next);
  };

  const moveQuickStripItem = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    if (fromIndex < 0 || toIndex < 0) return;
    if (fromIndex >= quickList.length || toIndex >= quickList.length) return;
    const next = [...quickList];
    const [removed] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, removed);
    updateSetting('home_quick_categories', next);
  };

  const setServiceItemEnabled = (index, enabled) => {
    const next = servicesList.map((row, i) => (i === index ? { ...row, enabled } : row));
    updateSetting('home_services_items', next);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Üst kategori şeridi</CardTitle>
              <CardDescription>
                Header altındaki yatay kaydırmalı görsel kategoriler. Satırları sürükleyip bırakarak
                sıralayın; ana sayfada da bu sıra kullanılır. Her satırda açık/kapalı anahtarı vardır.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="strip-master" className="text-sm whitespace-nowrap">
                Şerit görünsün
              </Label>
              <Switch
                id="strip-master"
                checked={settings?.quick_category_strip_enabled !== false}
                onCheckedChange={(v) => updateSetting('quick_category_strip_enabled', v)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {quickList.map((row, index) => {
            const meta = quickMetaById.get(row.id);
            if (!meta) return null;
            return (
              <div
                key={row.id}
                draggable
                onDragStart={(e) => {
                  setDragStripIndex(index);
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', String(index));
                }}
                onDragEnd={() => setDragStripIndex(null)}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  const from = parseInt(e.dataTransfer.getData('text/plain'), 10);
                  if (!Number.isNaN(from)) moveQuickStripItem(from, index);
                  setDragStripIndex(null);
                }}
                className={`flex items-center gap-3 justify-between rounded-lg border p-3 select-none cursor-grab active:cursor-grabbing ${
                  dragStripIndex === index ? 'opacity-60 ring-2 ring-primary/30' : ''
                }`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <GripVertical
                    className="h-5 w-5 flex-shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                  <span className="text-sm font-medium truncate">
                    {t(meta.nameKey)}
                    <span className="ml-2 text-xs text-muted-foreground font-mono whitespace-nowrap">
                      ({meta.id})
                    </span>
                  </span>
                </div>
                <Switch
                  checked={row.enabled !== false}
                  onCheckedChange={(v) => setQuickEnabled(row.id, v)}
                  disabled={settings?.quick_category_strip_enabled === false}
                />
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Hizmetler (kartlar)</CardTitle>
              <CardDescription>
                Ana sayfadaki büyük hizmet kartları. Üstteki «Bölüm açık» anahtarı tüm bölümü
                kapatır.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="svc-master" className="text-sm whitespace-nowrap">
                Bölüm açık
              </Label>
              <Switch
                id="svc-master"
                checked={settings?.services_enabled !== false}
                onCheckedChange={(v) => updateSetting('services_enabled', v)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label>Bölüm başlığı (ana sayfa)</Label>
            <Input
              value={settings?.services_title ?? ''}
              onChange={(e) => updateSetting('services_title', e.target.value)}
              placeholder="Hizmetlerimiz"
            />
          </div>
          {SERVICES_GRID_META.map((item, index) => (
            <div
              key={item.id}
              className="flex items-center justify-between gap-3 rounded-lg border p-3"
            >
              <span className="text-sm font-medium">
                {t(item.nameKey)}
                <span className="ml-2 text-xs text-muted-foreground font-mono">({item.id})</span>
              </span>
              <Switch
                checked={servicesList[index]?.enabled !== false}
                onCheckedChange={(v) => setServiceItemEnabled(index, v)}
                disabled={settings?.services_enabled === false}
              />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default HomePageStripAndServicesTab;
